/**
 * Variant Image Carousel Component
 *
 * Handles variant image carousel functionality with a THREE-ITEM VISIBLE LAYOUT.
 *
 * THREE-ITEM LAYOUT STRATEGY:
 * ============================
 * The carousel always displays exactly 3 variant items fully visible at once.
 * This is achieved through:
 * - Fixed container width calculated as: (item-width × 3) + (gap × 2)
 * - Smart positioning logic that adapts based on current selection
 * - Responsive sizing that maintains the 3-item layout across all breakpoints
 *
 * Positioning behavior:
 * - ≤3 items total: All items centered in container
 * - First item selected: Show first 3 items aligned left
 * - Last item selected: Show last 3 items aligned right
 * - Middle item selected: Show current item centered with one item on each side
 * - Smooth transitions between positions
 *
 * Additional features:
 * - Navigation arrows for scrolling through variants
 * - Bidirectional syncing between carousel images and swatches
 * - Keyboard navigation support (Arrow keys, Home, End, Enter, Space)
 * - Accessibility features (ARIA attributes, focus management)
 * - Responsive behavior across all breakpoints (Desktop: 110px, Tablet: 90px, Mobile: 75px)
 * - Theme editor compatibility
 * - Support for filtered items (hide_unselected_media feature)
 *
 * @fires variant:change - When variant selection changes
 */

class VariantImageCarousel extends HTMLElement {
  constructor() {
    super();

    // Configuration
    this.optionIndex = parseInt(this.dataset.optionIndex || "0");
    this.productId = this.dataset.productId;
    this.hideUnselectedMedia = this.hasAttribute("hide-unselected-media");

    // Parent <variant-picker> and the variant availability map (used to auto-select
    // the first in-stock size when a colour is chosen, avoiding dead-end selections).
    this.variantPicker = this.closest("variant-picker");
    this.availability = this.loadAvailability();

    // DOM elements
    this.track = this.querySelector(".variant-carousel__track");
    this.trackContainer = this.querySelector(
      ".variant-carousel__track-container"
    );
    this.items = Array.from(this.querySelectorAll(".variant-carousel__item"));
    this.prevButton = this.querySelector("[data-carousel-prev]");
    this.nextButton = this.querySelector("[data-carousel-next]");

    // State
    this.currentIndex = 0;
    this.isInitialized = false;
    this.isTransitioning = false;
    this.isFocusingFromNav = false;
    this.transitionEndCleanup = null;
    this.pointerSelectionCleanup = null;
    this.ignoreClickUntil = 0;
    this.ignoreFocusUntil = 0;

    this.init();
  }

  init() {
    if (!this.track || this.items.length === 0) {
      console.warn("Variant carousel: No track or items found");
      return;
    }

    // Apply initial filtering if hide_unselected_media is enabled
    if (this.hideUnselectedMedia) {
      this.applyInitialFiltering();
      // The carousel only renders when there are >5 total items, so we should
      // always be able to keep at least 3 *real* items visible (no placeholders).
      this.ensureMinimumVisibleItems(3);
    }

    // Ensure initial order shows: Last, Variant 1, Variant 2 (among visible items)
    this.applyInitialCircularOrdering();

    // Find initially selected item (among visible items)
    const activeItem = this.items.find(
      (item) =>
        item.classList.contains("variant-carousel__item--active") &&
        !item.hasAttribute("hidden")
    );
    if (activeItem) {
      this.currentIndex = this.items.indexOf(activeItem);
    } else {
      // If no active item found, select the first visible item
      const firstVisibleItem = this.items.find(
        (item) => !item.hasAttribute("hidden")
      );
      if (firstVisibleItem) {
        this.currentIndex = this.items.indexOf(firstVisibleItem);
      }
    }

    // Set up event listeners
    this.setupCarouselNavigation();
    this.setupItemInteraction();
    this.setupSwatchSync();
    this.setupKeyboardNavigation();

    // Mark as initialized
    this.isInitialized = true;

    // Initial positioning with a small delay to ensure layout is ready
    requestAnimationFrame(() => {
      this.updateCarousel(false); // false = no animation on init
    });
  }

  /**
   * Apply initial filtering based on filtered-indexes attribute
   */
  applyInitialFiltering() {
    try {
      const filteredIndexesAttr = this.getAttribute("filtered-indexes");
      if (!filteredIndexesAttr) return;

      const filteredIndexes = JSON.parse(filteredIndexesAttr);
      if (!Array.isArray(filteredIndexes)) return;

      // Hide items that are in the filtered indexes list
      this.items.forEach((item, index) => {
        const shouldHide =
          filteredIndexes.includes(index.toString()) ||
          filteredIndexes.includes(index);
        if (shouldHide) {
          item.setAttribute("hidden", "");
        }
      });
    } catch (e) {
      console.error("Variant carousel: Error applying initial filtering", e);
    }
  }

  /**
   * Get filtered indexes from attribute
   */
  get filteredIndexes() {
    try {
      const attr = this.getAttribute("filtered-indexes");
      if (!attr) return [];
      const parsed = JSON.parse(attr);
      return Array.isArray(parsed) ? parsed.map((idx) => parseInt(idx)) : [];
    } catch (e) {
      return [];
    }
  }

  /**
   * Filter carousel items by showing only items matching the selected variant
   * @param {Array} indexesToHide - Array of item indexes to hide
   */
  filterItems(indexesToHide = []) {
    this.items.forEach((item, index) => {
      const shouldHide =
        indexesToHide.includes(index) ||
        indexesToHide.includes(index.toString());
      item.toggleAttribute("hidden", shouldHide);
    });

    // If filtering would result in fewer than 3 visible items, unhide the
    // nearest real items (no placeholders). This is important because the
    // carousel only exists when there are >5 items total.
    const normalizedHidden = this.ensureMinimumVisibleItems(3, indexesToHide);

    // Update the filtered-indexes attribute (keep it in sync with DOM)
    this.setAttribute("filtered-indexes", JSON.stringify(normalizedHidden));

    // Ensure the current index points to a visible item
    this.ensureVisibleCurrentIndex();

    // Update carousel position
    this.updateCarousel(true);
  }

  /**
   * Ensure there are at least `minVisible` non-hidden items.
   *
   * When hide_unselected_media is enabled, upstream logic can attempt to hide
   * almost everything (often leaving 1 visible item). Since this carousel only
   * renders when there are >5 items total, we can always keep 3 real items
   * visible by unhiding the nearest neighbors instead of creating placeholders.
   *
   * @param {number} minVisible
   * @param {Array<number|string>|null} requestedHiddenIndexes - Optional list of
   *   indexes that upstream requested to hide. If provided, this method will
   *   return an updated list that matches the final DOM state.
   * @returns {Array<number>} Updated hidden index list (numeric), if
   *   requestedHiddenIndexes was provided; otherwise returns the current hidden
   *   indexes based on DOM state.
   */
  ensureMinimumVisibleItems(minVisible = 3, requestedHiddenIndexes = null) {
    if (!this.items?.length) return [];

    // Normalize requested hidden list for easier manipulation.
    let hiddenSet = null;
    if (Array.isArray(requestedHiddenIndexes)) {
      hiddenSet = new Set(
        requestedHiddenIndexes
          .map((v) => (typeof v === "string" ? parseInt(v, 10) : v))
          .filter((n) => Number.isFinite(n))
      );
    }

    const getVisibleIndexes = () =>
      this.items
        .map((item, idx) => ({ item, idx }))
        .filter(({ item }) => !item.hasAttribute("hidden"))
        .map(({ idx }) => idx);

    let visibleIndexes = getVisibleIndexes();
    if (visibleIndexes.length >= minVisible) {
      return hiddenSet
        ? Array.from(hiddenSet.values()).sort((a, b) => a - b)
        : this.items
            .map((item, idx) => (item.hasAttribute("hidden") ? idx : null))
            .filter((v) => v !== null);
    }

    // Anchor around the current index when possible.
    const len = this.items.length;
    const anchor =
      Number.isFinite(this.currentIndex) && this.currentIndex >= 0
        ? this.currentIndex
        : 0;

    const circularDistance = (a, b) => {
      const d = Math.abs(a - b);
      return Math.min(d, len - d);
    };

    // Candidate indexes are currently hidden items.
    const hiddenIndexes = this.items
      .map((item, idx) => (item.hasAttribute("hidden") ? idx : null))
      .filter((v) => v !== null);

    // Unhide nearest neighbors until we reach the minimum.
    hiddenIndexes
      .sort((a, b) => circularDistance(a, anchor) - circularDistance(b, anchor))
      .some((idx) => {
        if (visibleIndexes.length >= minVisible) return true;

        this.items[idx].removeAttribute("hidden");
        if (hiddenSet) hiddenSet.delete(idx);
        visibleIndexes.push(idx);
        return false;
      });

    return hiddenSet
      ? Array.from(hiddenSet.values()).sort((a, b) => a - b)
      : this.items
          .map((item, idx) => (item.hasAttribute("hidden") ? idx : null))
          .filter((v) => v !== null);
  }

  /**
   * Ensure currentIndex points to a visible item
   */
  ensureVisibleCurrentIndex() {
    const currentItem = this.items[this.currentIndex];
    if (currentItem && currentItem.hasAttribute("hidden")) {
      // Find the nearest visible item
      const visibleItems = this.items.filter(
        (item) => !item.hasAttribute("hidden")
      );
      if (visibleItems.length > 0) {
        const nearestVisible = visibleItems.reduce((prev, curr) => {
          const prevDist = Math.abs(
            this.items.indexOf(prev) - this.currentIndex
          );
          const currDist = Math.abs(
            this.items.indexOf(curr) - this.currentIndex
          );
          return currDist < prevDist ? curr : prev;
        });
        this.currentIndex = this.items.indexOf(nearestVisible);
        this.updateActiveItem(this.currentIndex);
      }
    }
  }

  setupCarouselNavigation() {
    if (this.prevButton) {
      this.prevButton.addEventListener("click", () => {
        this.applyArrowClickTrackStyles(this.prevButton);
        this.navigatePrev();
      });
    }

    if (this.nextButton) {
      this.nextButton.addEventListener("click", () => {
        this.applyArrowClickTrackStyles(this.nextButton);
        this.navigateNext();
      });
    }
  }

  /**
   * Ensure arrow clicks always animate the transform.
   * Some non-animated updates (init/resize) temporarily set an inline
   * "transition: none" which can race with a button click.
   */
  applyArrowClickTrackStyles(clickedButton = null) {
    if (!this.track) return;

    // Respect reduced motion
    try {
      if (
        window.matchMedia &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches
      ) {
        return;
      }
    } catch {
      // no-op
    }

    // Trigger an explicit "clicked" animation on the arrow button itself.
    // This provides feedback on touch devices (no hover) and matches hover/focus behavior.
    if (this.prevButton) this.prevButton.classList.remove("is-clicked");
    if (this.nextButton) this.nextButton.classList.remove("is-clicked");
    if (clickedButton) {
      clickedButton.classList.add("is-clicked");
      clearTimeout(this.arrowClickButtonTimeout);
      this.arrowClickButtonTimeout = setTimeout(() => {
        clickedButton.classList.remove("is-clicked");
      }, 250);
    }

    // Add a CSS-driven "force animate" class for this click
    this.track.classList.add("is-arrow-clicking");

    // Also set inline styles as a safety net in case other CSS overrides exist
    this.track.style.transition =
      "transform 0.42s cubic-bezier(0.22, 1, 0.36, 1)";
    this.track.style.willChange = "transform";

    // Remove the class shortly after the click so it doesn't become "sticky"
    clearTimeout(this.arrowClickStyleTimeout);
    this.arrowClickStyleTimeout = setTimeout(() => {
      // Remove the forced styles so the default CSS can take over
      this.track?.classList.remove("is-arrow-clicking");
      if (this.track) {
        this.track.style.transition = "";
        this.track.style.willChange = "";
      }
    }, 500);
  }

  setupItemInteraction() {
    this.items.forEach((item, index) => {
      item.addEventListener("pointerdown", (event) => {
        if (
          event.pointerType === "mouse" &&
          typeof event.button === "number" &&
          event.button !== 0
        ) {
          return;
        }

        if (!this.isSelectableItem(item)) return;

        this.startPointerSelection(event, index);
      });

      // Click handler
      item.addEventListener("click", () => {
        if (this.shouldIgnoreClick()) return;

        if (this.isSelectableItem(item)) {
          this.selectItem(index);
        }
      });

      // Focus handler for accessibility
      item.addEventListener("focus", () => {
        if (this.isFocusingFromNav || this.shouldIgnoreFocus()) return;
        this.currentIndex = index;
        this.updateCarousel();
      });
    });
  }

  shouldIgnoreClick() {
    return this.ignoreClickUntil && performance.now() < this.ignoreClickUntil;
  }

  shouldIgnoreFocus() {
    return this.ignoreFocusUntil && performance.now() < this.ignoreFocusUntil;
  }

  startPointerSelection(event, index) {
    this.clearPointerSelection();

    // Prevent focus-driven scrolling from racing ahead of selection.
    this.ignoreFocusUntil = performance.now() + 350;

    const item = this.items[index];
    if (!this.isSelectableItem(item)) return;

    const pointerType = event.pointerType || "mouse";
    const isTouch = pointerType === "touch";

    // Mouse/pen interactions should select immediately for zero latency.
    if (!isTouch) {
      // Skip the upcoming click event to avoid double selection.
      this.ignoreClickUntil = performance.now() + 350;
      this.selectItem(index);
      return;
    }

    const pointerId = event.pointerId;
    const startX = event.clientX;
    const startY = event.clientY;
    const moveThreshold = 6;
    let hasMoved = false;

    if (typeof item.setPointerCapture === "function") {
      try {
        item.setPointerCapture(pointerId);
      } catch {
        // no-op
      }
    }

    const onPointerMove = (moveEvent) => {
      if (moveEvent.pointerId !== pointerId) return;

      const dx = moveEvent.clientX - startX;
      const dy = moveEvent.clientY - startY;
      if (dx * dx + dy * dy > moveThreshold * moveThreshold) {
        hasMoved = true;
      }
    };

    const finish = (endEvent) => {
      if (endEvent.pointerId !== pointerId) return;
      cleanup();

      if (hasMoved) return;

      // Skip the upcoming click event to avoid double selection.
      this.ignoreClickUntil = performance.now() + 350;
      this.selectItem(index);
    };

    const cancel = (cancelEvent) => {
      if (cancelEvent.pointerId !== pointerId) return;
      cleanup();
    };

    const cleanup = () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", finish);
      window.removeEventListener("pointercancel", cancel);
      this.pointerSelectionCleanup = null;

      if (typeof item.releasePointerCapture === "function") {
        try {
          item.releasePointerCapture(pointerId);
        } catch {
          // no-op
        }
      }
    };

    this.pointerSelectionCleanup = cleanup;

    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", finish);
    window.addEventListener("pointercancel", cancel);
  }

  clearPointerSelection() {
    if (typeof this.pointerSelectionCleanup === "function") {
      this.pointerSelectionCleanup();
    }
  }

  setupSwatchSync() {
    // Find the corresponding option selector (fieldset containing this carousel)
    const optionFieldset = this.closest("fieldset.variant-picker__option");
    if (!optionFieldset) return;

    this.syncWithSwatches(optionFieldset);
  }

  syncWithSwatches(optionFieldset) {
    // Use a delegated listener on the fieldset so it keeps working even if the
    // swatch radios are replaced (e.g. after the theme re-renders the picker).
    optionFieldset.addEventListener("change", (e) => {
      const input = e.target;
      if (!input || input.type !== "radio") return;

      // When a colour is selected directly via the native swatch, make sure a
      // valid (in-stock) size is selected for it BEFORE the theme reads the form,
      // so we never land on a sold-out combination.
      if (!this.isUpdatingFromCarousel) {
        this.ensureAvailableSizeForOption(input.dataset.optionText);
      }

      // Prevent recursive updates coming from our own selectItem().
      if (this.isUpdatingFromCarousel) return;

      this.syncCarouselToOptionValue({
        optionValueId: input.value,
        optionText: input.dataset.optionText,
      });
    });

    // Also listen for variant picker changes at the form level. This covers cases
    // where the selection changes without a direct swatch "change" in this fieldset
    // (e.g. navigation, combined-product swaps, or programmatic updates).
    const variantPicker = this.variantPicker || this.closest("variant-picker");
    if (variantPicker) {
      const formId = variantPicker.getAttribute("form-id");
      const form = formId ? document.forms[formId] : null;

      if (form) {
        form.addEventListener("variant:change", (e) => {
          if (this.isUpdatingFromCarousel) return;

          const variant = e.detail?.variant;
          const optionText = variant?.options?.[this.optionIndex];
          if (optionText != null) {
            this.syncCarouselToOptionValue({ optionText });
          }
        });
      }
    }
  }

  /**
   * Find the carousel item matching a given option value (by id or text) and make
   * it the active/centered item.
   */
  syncCarouselToOptionValue({ optionValueId = null, optionText = null } = {}) {
    const normalizedText = optionText?.toLowerCase();

    const itemIndex = this.items.findIndex((item) => {
      if (optionValueId && item.dataset.optionValue === optionValueId) {
        return true;
      }
      if (optionText != null) {
        if (item.dataset.optionText === optionText) return true;
        if (item.dataset.optionText?.toLowerCase() === normalizedText) {
          return true;
        }
        if (item.dataset.color?.toLowerCase() === normalizedText) return true;
      }
      return false;
    });

    if (itemIndex !== -1 && itemIndex !== this.currentIndex) {
      this.currentIndex = itemIndex;
      this.updateActiveItem(itemIndex);
      this.updateCarousel(true); // animate + center the active thumbnail
    }
  }

  /**
   * Parse the availability map emitted by the variant picker. Each entry is
   * `{ options: [<value text per position>], available: <bool> }`.
   */
  loadAvailability() {
    try {
      const picker = this.variantPicker || this.closest("variant-picker");
      const script = picker?.querySelector(
        "script[data-variant-availability]"
      );
      if (!script) return [];
      const parsed = JSON.parse(script.textContent || "[]");
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      console.warn("Variant carousel: Could not parse availability data", e);
      return [];
    }
  }

  /**
   * Given a newly chosen value for THIS carousel's option (typically colour),
   * ensure the other options (typically size) point to an in-stock combination.
   * If the currently selected combination is already available, nothing changes.
   *
   * @param {string} optionText - The option value text being selected (e.g. colour).
   * @returns {boolean} true if it selected/kept an available combination.
   */
  ensureAvailableSizeForOption(optionText) {
    if (optionText == null || !this.availability.length) return true;

    const scope = this.variantPicker || this.closest("variant-picker");
    if (!scope) return true;
    const optionPos = this.optionIndex; // 0-based position of this option

    // Current selection (value text) per 0-based option position.
    const currentByPos = {};
    scope
      .querySelectorAll("input[data-option-position]:checked")
      .forEach((input) => {
        const pos = parseInt(input.dataset.optionPosition, 10) - 1;
        if (Number.isFinite(pos)) currentByPos[pos] = input.dataset.optionText;
      });

    // The combination we're about to have once this option is applied.
    const prospective = { ...currentByPos, [optionPos]: optionText };

    const matches = (variantOptions, combo) =>
      Object.keys(combo).every((pos) => {
        const p = parseInt(pos, 10);
        return combo[p] == null || variantOptions[p] === combo[p];
      });

    // Already a valid, in-stock combination -> keep the user's current sizes.
    if (this.availability.some((v) => v.available && matches(v.options, prospective))) {
      return true;
    }

    // Otherwise pick the first in-stock variant for this option value and align
    // the other option radios (sizes) to it.
    const firstAvailable = this.availability.find(
      (v) => v.available && v.options[optionPos] === optionText
    );
    if (!firstAvailable) return false; // entire option value is sold out

    firstAvailable.options.forEach((valueText, pos) => {
      if (pos === optionPos) return;
      const radios = Array.from(
        scope.querySelectorAll(`input[data-option-position="${pos + 1}"]`)
      );
      const target = radios.find((r) => r.dataset.optionText === valueText);
      if (target && !target.checked) {
        target.checked = true;
      }
    });

    return true;
  }

  setupKeyboardNavigation() {
    this.items.forEach((item, index) => {
      item.addEventListener("keydown", (e) => {
        let newIndex = index;

        switch (e.key) {
          case "ArrowLeft":
            e.preventDefault();
            // Find previous visible item
            newIndex = index - 1;
            while (
              newIndex >= 0 &&
              this.items[newIndex].hasAttribute("hidden")
            ) {
              newIndex--;
            }
            if (newIndex < 0) newIndex = index; // Stay at current if no previous visible
            break;
          case "ArrowRight":
            e.preventDefault();
            // Find next visible item
            newIndex = index + 1;
            while (
              newIndex < this.items.length &&
              this.items[newIndex].hasAttribute("hidden")
            ) {
              newIndex++;
            }
            if (newIndex >= this.items.length) newIndex = index; // Stay at current if no next visible
            break;
          case "Home":
            e.preventDefault();
            // Find first visible item
            newIndex = 0;
            while (
              newIndex < this.items.length &&
              this.items[newIndex].hasAttribute("hidden")
            ) {
              newIndex++;
            }
            break;
          case "End":
            e.preventDefault();
            // Find last visible item
            newIndex = this.items.length - 1;
            while (
              newIndex >= 0 &&
              this.items[newIndex].hasAttribute("hidden")
            ) {
              newIndex--;
            }
            break;
          case "Enter":
          case " ":
            e.preventDefault();
            if (
              !item.classList.contains("variant-carousel__item--unavailable")
            ) {
              this.selectItem(index);
            }
            return;
          default:
            return;
        }

        if (
          newIndex !== index &&
          newIndex >= 0 &&
          newIndex < this.items.length
        ) {
          this.items[newIndex].focus();
          this.currentIndex = newIndex;
          this.updateCarousel();
        }
      });
    });
  }

  selectItem(index) {
    if (index < 0 || index >= this.items.length) return;

    const item = this.items[index];
    if (
      item.hasAttribute("hidden") ||
      item.classList.contains("variant-carousel__item--unavailable")
    ) {
      return;
    }
    const optionValueId = item.dataset.optionValue; // This is the option value ID
    const optionText = item.dataset.optionText;

    if (!optionValueId) return;

    // Update carousel state
    this.currentIndex = index;
    this.updateActiveItem(index);
    this.updateCarousel(true);

    // Sync with variant picker - find and check the corresponding radio button in the same fieldset
    const optionFieldset = this.closest("fieldset.variant-picker__option");
    if (!optionFieldset) {
      console.warn("Variant carousel: Could not find parent fieldset");
      return;
    }

    const allRadios = optionFieldset.querySelectorAll('input[type="radio"]');
    const matchingRadio = Array.from(allRadios).find((radio) => {
      // Match by option value ID (radio buttons use value.id)
      return radio.value === optionValueId;
    });

    if (matchingRadio && !matchingRadio.checked) {
      // Set flag to prevent recursive updates ONLY when we're about to update
      this.isUpdatingFromCarousel = true;

      // Auto-select an in-stock size for this colour (if the current size is sold
      // out for it) BEFORE checking the colour radio, so the theme resolves to an
      // available variant rather than a dead-end sold-out combination.
      this.ensureAvailableSizeForOption(optionText);

      matchingRadio.checked = true;

      // Dispatch change event to trigger variant picker updates
      matchingRadio.dispatchEvent(new Event("change", { bubbles: true }));

      // Also dispatch input event for additional compatibility
      matchingRadio.dispatchEvent(new Event("input", { bubbles: true }));

      // Emit a custom event to notify other components
      this.dispatchEvent(
        new CustomEvent("carousel:variant-select", {
          bubbles: true,
          detail: {
            index: index,
            optionValueId: optionValueId,
            optionText: optionText,
            variantId: item.dataset.variantId,
          },
        })
      );

      // Reset flag after transitions + variant updates have propagated
      setTimeout(() => {
        this.isUpdatingFromCarousel = false;
      }, 500);
    }
  }

  updateActiveItem(index) {
    this.items.forEach((item, i) => {
      if (i === index) {
        item.classList.add("variant-carousel__item--active");
        item.setAttribute("aria-current", "true");
      } else {
        item.classList.remove("variant-carousel__item--active");
        item.removeAttribute("aria-current");
      }
    });
  }

  navigatePrev() {
    const prevIndex = this.findPrevSelectableIndex(this.currentIndex);
    if (prevIndex === -1 || prevIndex === this.currentIndex) return;

    this.selectItem(prevIndex);
    this.focusItemFromNav(prevIndex);
  }

  navigateNext() {
    const nextIndex = this.findNextSelectableIndex(this.currentIndex);
    if (nextIndex === -1 || nextIndex === this.currentIndex) return;

    this.selectItem(nextIndex);
    this.focusItemFromNav(nextIndex);
  }

  updateCarousel(animate = true) {
    if (!this.items.length || !this.track) return;

    // Allow updates during transitions (cancel previous tracking)
    if (animate && this.isTransitioning) {
      this.cancelTransitionTracking();
      this.isTransitioning = false;
    }

    // Temporarily disable transitions if not animating (e.g., on init)
    if (!animate) {
      this.track.style.transition = "none";
    }

    // Set transitioning flag
    if (animate) {
      this.isTransitioning = true;
    }

    // Calculate translation to position the current item
    const currentItem = this.items[this.currentIndex];
    if (!currentItem) {
      // Clear transitioning flag before early return
      if (animate) {
        this.isTransitioning = false;
      }
      return;
    }

    const itemWidth = currentItem.offsetWidth || 110;
    const gap = this.getGapSize();
    const containerWidth = this.trackContainer
      ? this.trackContainer.offsetWidth
      : 0;

    if (containerWidth === 0) {
      // Clear transitioning flag before retry
      if (animate) {
        this.isTransitioning = false;
      }
      // Container not ready, try again later
      requestAnimationFrame(() => this.updateCarousel(animate));
      return;
    }

    // Get container padding to account for it in positioning calculations
    const containerPadding = this.getContainerPadding();

    // Get visible items (non-hidden)
    const visibleItems = this.items.filter(
      (item) => !item.hasAttribute("hidden")
    );
    const visibleCount = visibleItems.length;
    const currentVisibleIndex = visibleItems.indexOf(currentItem);

    // Use standard item width for consistent calculations
    const standardItemWidth = this.getStandardItemWidth();

    // Calculate cumulative offset for all visible items before current
    let itemOffset = 0;
    for (let i = 0; i < this.currentIndex; i++) {
      const item = this.items[i];
      if (!item.hasAttribute("hidden")) {
        itemOffset += (item?.offsetWidth || standardItemWidth) + gap;
      }
    }

    // Calculate total width of visible items
    let totalTrackWidth = 0;
    let visibleGaps = 0;
    for (let i = 0; i < this.items.length; i++) {
      const item = this.items[i];
      if (!item.hasAttribute("hidden")) {
        totalTrackWidth += item?.offsetWidth || standardItemWidth;
        // Count gaps between visible items
        let nextVisibleIndex = i + 1;
        while (
          nextVisibleIndex < this.items.length &&
          this.items[nextVisibleIndex]?.hasAttribute("hidden")
        ) {
          nextVisibleIndex++;
        }
        if (nextVisibleIndex < this.items.length) {
          visibleGaps++;
        }
      }
    }
    totalTrackWidth += visibleGaps * gap;

    // Calculate available width for content (excluding padding)
    const availableWidth = containerWidth - containerPadding.left - containerPadding.right;

    let translation;

    // Strategy: Always try to show exactly 3 items, with the selected item optimally positioned
    if (visibleCount <= 3) {
      // If we have 3 or fewer items, center them all in the available space
      translation = Math.max(0, (availableWidth - totalTrackWidth) / 2);
    } else {
      // More than 3 items: use smart positioning
      // The goal is to show the current item with one item on each side when possible

      if (currentVisibleIndex === 0) {
        // First item selected: align to left to show first 3 items
        translation = 0;
      } else if (currentVisibleIndex === visibleCount - 1) {
        // Last item selected: align to right to show last 3 items
        translation = availableWidth - totalTrackWidth;
      } else if (currentVisibleIndex === 1) {
        // Second item selected: show items 0, 1, 2 (first 3)
        translation = 0;
      } else if (currentVisibleIndex === visibleCount - 2) {
        // Second to last item: show last 3 items
        translation = availableWidth - totalTrackWidth;
      } else {
        // Middle items: Position current item in the center slot of the 3-item view
        // Calculate position to place current item as the middle of 3 visible items
        const targetPosition = standardItemWidth + gap; // This is where the center item starts
        translation = targetPosition - itemOffset;

        // Constrain translation to valid range
        const maxTranslation = 0; // Don't scroll past the beginning
        const minTranslation = availableWidth - totalTrackWidth; // Don't scroll past the end
        translation = Math.min(
          maxTranslation,
          Math.max(minTranslation, translation)
        );
      }
    }

    // Apply transform
    this.track.style.transform = `translate3d(${translation}px, 0, 0)`;

    // Re-enable transitions after a frame if we disabled them
    if (!animate) {
      requestAnimationFrame(() => {
        this.track.style.transition = "";
      });
    }

    // Clear transitioning flag after animation completes
    if (animate) {
      this.trackTransition(translation);
    }

    // Update arrow button states
    this.updateArrowStates();

    // Update item visibility for performance
    this.updateItemVisibility();
  }

  getGapSize() {
    const width = window.innerWidth;
    if (width < 480) return 8;
    if (width < 750) return 10;
    return 6;
  }

  /**
   * Get the container's left and right padding values
   * This ensures positioning calculations account for the padded area
   */
  getContainerPadding() {
    if (!this.trackContainer) {
      return { left: 0, right: 0 };
    }

    const computedStyle = window.getComputedStyle(this.trackContainer);
    const paddingLeft = parseFloat(computedStyle.paddingLeft) || 0;
    const paddingRight = parseFloat(computedStyle.paddingRight) || 0;

    return {
      left: paddingLeft,
      right: paddingRight,
    };
  }

  /**
   * Get the standard item width based on viewport size
   * This ensures consistent calculations for the 3-item layout
   */
  getStandardItemWidth() {
    const width = window.innerWidth;
    if (width < 480) return 75; // Mobile
    if (width < 750) return 90; // Tablet
    return 110; // Desktop
  }

  /**
   * Calculate the ideal container width for showing exactly 3 items
   */
  getIdealContainerWidth() {
    const itemWidth = this.getStandardItemWidth();
    const gap = this.getGapSize();
    return itemWidth * 3 + gap * 2;
  }

  updateArrowStates() {
    const visibleSelectableCount = this.items.filter((item) =>
      this.isSelectableItem(item)
    ).length;
    const shouldDisable = visibleSelectableCount <= 1;

    if (this.prevButton) {
      this.prevButton.disabled = shouldDisable;
      this.prevButton.setAttribute("aria-disabled", shouldDisable);
    }

    if (this.nextButton) {
      this.nextButton.disabled = shouldDisable;
      this.nextButton.setAttribute("aria-disabled", shouldDisable);
    }
  }

  updateItemVisibility() {
    // Update visibility based on hidden attribute
    this.items.forEach((item) => {
      if (item.hasAttribute("hidden")) {
        item.style.visibility = "hidden";
      } else {
        item.style.visibility = "visible";
      }
    });
  }

  // Handle window resize and theme editor changes
  connectedCallback() {
    if (!this.trackContainer) {
      return;
    }

    // Use ResizeObserver to handle container size changes
    this.resizeObserver = new ResizeObserver(() => {
      // Debounce updates during resize
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => {
        this.updateCarousel(false); // Don't animate during resize
      }, 100);
    });

    this.resizeObserver.observe(this.trackContainer);

    // Listen for Shopify theme editor events
    // Store bound function references so they can be properly removed later
    if (typeof Shopify !== "undefined" && Shopify.designMode) {
      this.boundHandleSectionLoad = this.handleSectionReload.bind(this);
      this.boundHandleSectionSelect = this.handleSectionReload.bind(this);

      document.addEventListener(
        "shopify:section:load",
        this.boundHandleSectionLoad
      );
      document.addEventListener(
        "shopify:section:select",
        this.boundHandleSectionSelect
      );
    }
  }

  disconnectedCallback() {
    this.clearPointerSelection();
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    if (this.arrowClickStyleTimeout) {
      clearTimeout(this.arrowClickStyleTimeout);
    }
    if (this.arrowClickButtonTimeout) {
      clearTimeout(this.arrowClickButtonTimeout);
    }

    // Remove event listeners using the stored bound references
    if (typeof Shopify !== "undefined" && Shopify.designMode) {
      if (this.boundHandleSectionLoad) {
        document.removeEventListener(
          "shopify:section:load",
          this.boundHandleSectionLoad
        );
      }
      if (this.boundHandleSectionSelect) {
        document.removeEventListener(
          "shopify:section:select",
          this.boundHandleSectionSelect
        );
      }
    }
  }

  handleSectionReload() {
    // Reinitialize carousel after section reload in theme editor
    requestAnimationFrame(() => {
      this.updateCarousel(false);
    });
  }

  /**
   * Reorder items so initial visible order is: Last, First, Second.
   * This matches the requested page-load order and enables a natural "wrap" feel.
   */
  applyInitialCircularOrdering() {
    if (!this.track) return;

    const visibleItems = this.items.filter((item) => !item.hasAttribute("hidden"));
    if (visibleItems.length < 3) return;

    const firstVisible = visibleItems[0];
    const lastVisible = visibleItems[visibleItems.length - 1];
    if (!firstVisible || !lastVisible || firstVisible === lastVisible) return;

    // Move last visible item before the first visible item
    if (lastVisible.parentElement === this.track) {
      this.track.insertBefore(lastVisible, firstVisible);
      // Refresh item list so indexes align with DOM order
      this.items = Array.from(this.querySelectorAll(".variant-carousel__item"));
    }
  }

  isSelectableItem(item) {
    if (!item) return false;
    if (item.hasAttribute("hidden")) return false;
    if (item.classList.contains("variant-carousel__item--unavailable")) return false;
    return true;
  }

  findPrevSelectableIndex(fromIndex) {
    if (!this.items.length) return -1;

    // Scan left
    for (let i = fromIndex - 1; i >= 0; i--) {
      if (this.isSelectableItem(this.items[i])) return i;
    }

    // Wrap to end
    for (let i = this.items.length - 1; i > fromIndex; i--) {
      if (this.isSelectableItem(this.items[i])) return i;
    }

    return -1;
  }

  findNextSelectableIndex(fromIndex) {
    if (!this.items.length) return -1;

    // Scan right
    for (let i = fromIndex + 1; i < this.items.length; i++) {
      if (this.isSelectableItem(this.items[i])) return i;
    }

    // Wrap to start
    for (let i = 0; i < fromIndex; i++) {
      if (this.isSelectableItem(this.items[i])) return i;
    }

    return -1;
  }

  focusItemFromNav(index) {
    const item = this.items[index];
    if (!item) return;

    this.isFocusingFromNav = true;
    requestAnimationFrame(() => {
      try {
        item.focus({ preventScroll: true });
      } catch {
        item.focus();
      }
      requestAnimationFrame(() => {
        this.isFocusingFromNav = false;
      });
    });
  }

  getTrackTransitionDurationMs() {
    if (!this.track) return 0;
    const style = window.getComputedStyle(this.track);
    const durations = (style.transitionDuration || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

    if (!durations.length) return 0;

    // Use the first duration value
    const d = durations[0];
    if (d.endsWith("ms")) return parseFloat(d) || 0;
    if (d.endsWith("s")) return (parseFloat(d) || 0) * 1000;
    return 0;
  }

  cancelTransitionTracking() {
    if (typeof this.transitionEndCleanup === "function") {
      this.transitionEndCleanup();
      this.transitionEndCleanup = null;
    }
  }

  trackTransition() {
    if (!this.track) return;

    this.cancelTransitionTracking();

    const durationMs = this.getTrackTransitionDurationMs();
    let didFinish = false;

    const onEnd = (event) => {
      if (event && event.target !== this.track) return;
      if (event && event.propertyName !== "transform") return;
      finish();
    };

    const finish = () => {
      if (didFinish) return;
      didFinish = true;
      this.isTransitioning = false;
      this.track.removeEventListener("transitionend", onEnd);
      this.track.removeEventListener("transitioncancel", onEnd);
      if (fallbackTimer) clearTimeout(fallbackTimer);
      this.transitionEndCleanup = null;
    };

    this.track.addEventListener("transitionend", onEnd);
    this.track.addEventListener("transitioncancel", onEnd);

    const fallbackTimer = setTimeout(() => finish(), Math.max(0, durationMs) + 50);
    this.transitionEndCleanup = finish;
  }
}

// Register the custom element
if (!customElements.get("variant-image-carousel")) {
  customElements.define("variant-image-carousel", VariantImageCarousel);
}

// Initialize carousels on page load and in Shopify theme editor
function initializeCarousels() {
  document.querySelectorAll(".variant-image-carousel").forEach((carousel) => {
    if (!carousel.matches("variant-image-carousel")) {
      // Upgrade element if not already a custom element
      customElements.upgrade(carousel);
    }
  });
}

document.addEventListener("DOMContentLoaded", initializeCarousels);

// Also initialize when Shopify theme editor reloads sections
if (typeof Shopify !== "undefined" && Shopify.designMode) {
  document.addEventListener("shopify:section:load", initializeCarousels);
  document.addEventListener("shopify:section:reorder", initializeCarousels);
}
